import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { Button } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { NfcCard, Visitor } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import DataGridSelectionToolbar from 'v2/components/DataGrid/DataGridSelectionToolbar';
import { getOverviewDataGridProps } from 'v2/components/DataGrid/getOverviewDataGridProps';
import { DataGridSelectionWithPagination } from 'v2/components/DataGrid/useDataGridSelectionWithPagination';
import useModalContext from 'v2/components/Modal/useModalContext';
import { DATAGRID_ROW_HEIGHT } from 'v2/constants';
import useAllUsersColumns, {
  AllUsersOverviewRow,
  AllUsersOverviewVariant
} from 'v2/pages/Users/Overview/components/useAllUsersColumns';
import { getUserPath } from 'v2/paths';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';
import AddUsersToGroupsModal from '../modals/AddUsersToGroupsModal/AddUsersToGroupsModal';
import AssignRolesModal from '../modals/AssignRolesModal';
import DeleteUsersModal from '../modals/DeleteUsersModal/DeleteUsersModal';

const bulkActionModalPaperSx = {
  backgroundColor: (theme: Theme) => theme.custom.background.level1,
  backgroundImage: 'none'
};

interface Props {
  visitors: Visitor[];
  selection: DataGridSelectionWithPagination<AllUsersOverviewRow>;
  cards: NfcCard[];
  variant?: AllUsersOverviewVariant;
  loading: boolean;
  className?: string;
  groupId?: string;
}

const AllUsersOverview: React.FC<Props> = ({
  visitors,
  selection,
  cards,
  variant = 'Page',
  loading,
  className,
  groupId
}) => {
  const { translation } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModalContext();
  const { columns, columnVisibilityModel } = useAllUsersColumns({ cards, variant, groupId });

  const { gridRef, apiRef, rows, selectedIds, getGridProps, isCheckboxClick, toolbarProps, clearSelection } = selection;

  const selectedVisitors = useMemo(() => {
    const selectedVisitorIds = new Set(Array.from(selectedIds, String));
    return visitors.filter(({ id }) => selectedVisitorIds.has(id));
  }, [selectedIds, visitors]);
  const selectedUsers = selectedVisitors.map(({ id, name }) => ({ id, name }));

  const handleOpenAddUsersToGroupsModal = () =>
    openModal(<AddUsersToGroupsModal selectedUsers={selectedUsers} onSuccess={clearSelection} />, {
      width: '600px',
      aboveSnackbar: true,
      paperSx: bulkActionModalPaperSx
    });

  const handleOpenAssignRolesModal = () =>
    openModal(
      <AssignRolesModal
        selectedUsers={selectedUsers}
        hasKeySharingSelectedUsers={selectedVisitors.some(
          (visitor) => getVisitorOrigin(visitor) === VisitorOrigin.KeySharing
        )}
        onSuccess={clearSelection}
      />,
      { width: '444px', aboveSnackbar: true, paperSx: bulkActionModalPaperSx }
    );

  const handleOpenDeleteUsersModal = () =>
    openModal(<DeleteUsersModal selectedVisitors={selectedVisitors} onSuccess={clearSelection} />, {
      width: '444px',
      aboveSnackbar: true,
      paperSx: bulkActionModalPaperSx
    });

  const overviewDataGridProps = getOverviewDataGridProps({ rowCount: rows.length, loading });

  return (
    <>
      <DataGridSelectionToolbar
        {...toolbarProps}
        actions={
          <>
            <Button size="small" variant="outlined" color="primary" onClick={handleOpenAddUsersToGroupsModal}>
              {translation.add_to_groups}
            </Button>
            <Button size="small" variant="outlined" color="primary" onClick={handleOpenAssignRolesModal}>
              {translation.assign_roles}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlined />}
              onClick={handleOpenDeleteUsersModal}
            >
              {translation.delete}
            </Button>
          </>
        }
      />
      <DataGrid
        {...overviewDataGridProps}
        {...getGridProps(columns, className)}
        ref={gridRef}
        apiRef={apiRef}
        rows={rows}
        columnVisibilityModel={columnVisibilityModel}
        rowHeight={DATAGRID_ROW_HEIGHT}
        onRowClick={({ id }, event) => {
          if (isCheckboxClick(event)) return;
          navigate(getUserPath(String(id)));
        }}
        initialState={{
          ...overviewDataGridProps.initialState,
          sorting: { sortModel: [{ field: 'user', sort: 'asc' }] }
        }}
        disableColumnSelector
        disableColumnFilter
        hideFooterSelectedRowCount
      />
    </>
  );
};

export default AllUsersOverview;
